import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { notNull } from "@/helpers/utility/Type";

import { buildHTMLText, useCopyPaste } from "@core/composables/useCopyPaste";

import { isSceneElementNode } from "@editor/items/sceneElement/sceneElementFunctions";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useModelStore } from "@editor/items/model/model.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useImageStore } from "@editor/items/image/images.store";
import { useIndicatorStore } from "@editor/items/indicator/indicators.store";
import { useUndo } from "@editor/store/undo/undo.store";
import { useSceneTree } from "@editor/items/scene/sceneTree";
import { useSceneNodesStore } from "@editor/items/scene/sceneNodes.store";
import { useSceneTextStore } from "@editor/items/sceneText/sceneTexts.store";
import { useSceneMediumStore } from "@editor/items/sceneMedium/sceneMedia.store";

import type { TargetItem } from "@editor/items/target/TargetItem";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { ImageItem } from "@editor/items/image/ImageItem";
import type { SceneNodeItem } from "@editor/items/scene/SceneNodeItem";
import type { Uid } from "@/helpers/utility/Id";
import type { ChartItem } from "@editor/items/charts/ChartItem";
import { isSceneChartNode } from "@editor/items/sceneChart/sceneChartFunctions";
import { useChartStore } from "@editor/items/charts/charts.store";
import { isSceneMediumNode } from "@editor/items/sceneMedium/sceneMediumFunctions";
import { useMediaStore } from "@core/media/media.store";

type PastableItem = (TargetItem | IndexItem | ResourceItem | ImageItem | SceneNodeItem | ChartItem) & {
  // add a reference to the original item, so we can update the reference when pasting
  _referencedBy?: Uid<string>[];
};
type PastableItemType = PastableItem["type"] | "element";
const pastableItemTypes = new Set<PastableItemType>([
  "element",
  "target",
  "index",
  "resource",
  "chart",
  "sceneNode"
]);

/**
 * Adds copy/paste functionality to the model.
 */
export function useModelCopyPaste() {
  const { t } = useI18n();
  const toast = useToast();

  const sceneTree = useSceneTree();
  const modelStore = useModelStore();
  const imageStore = useImageStore();
  const indexStore = useIndexStore();
  const targetStore = useTargetStore();
  const resourceStore = useResourceStore();
  const selectionStore = useSelectionStore();
  const indicatorStore = useIndicatorStore();
  const sceneTextStore = useSceneTextStore();
  const sceneMediumStore = useSceneMediumStore();
  const sceneNodeStore = useSceneNodesStore();

  const { onCopyEvent, onPasteEvent, copy } = useCopyPaste();

  /**
   * Called when the user presses CTRL/CMD + C.
   * Copies the selected items to the clipboard.
   */
  onCopyEvent(() => {
    // get all copyable items from the selection:
    const copyableItems = getCopyableItems(selectionStore.selectedItems);
    const copyData = getCopyData(copyableItems);
    if (copyData.data.length == 0) {
      console.warn("No data to copy");
      return;
    }

    notifyAboutCopy(copyableItems);

    return copyData;
  });

  function getCopyableItems(items: { type: string; id: number }[]) {
    const copyableItems: PastableItem[] = items.filter(
        (i): i is { type: "target" | "resource" | "index" | "chart" | "sceneNode"; id: number } =>
          (pastableItemTypes as Set<string>).has(i.type)
      )
      .map((i) => modelStore.getItem(i.type, i.id))
      .filter(notNull);
    return copyableItems;
  }

  function getCopyData(items: PastableItem[]) {
    const itemsToCopy = items.slice();

    // add images to the items to be copied
    for (let i = 0; i < itemsToCopy.length; i++) {
      const item = itemsToCopy[i];
      if (!("imageId" in item)) continue;
      if (item.imageId == null) continue;
      const image = imageStore.items[item.imageId];
      if (!image) continue;
      if (image.mediaUUID == null) continue;

      // include the media data in the image
      const mediaData = imageStore.mediaData[image.id];

      // add the image to the items to be copied
      itemsToCopy.splice(i, 0, { ...image, data: mediaData, _referencedBy: [item.uid] });
      i++;
    }

    for (let i = 0; i < itemsToCopy.length; i++) {
      const item = itemsToCopy[i];
      if (item.type === "sceneNode") {
        const sceneNode = item;
        if (isSceneElementNode(sceneNode)) {
          const elementData = sceneNode.data;
          // Add the element item to the items to be copied
          const elementItem = modelStore.getItem(elementData.elementUid);
          if (elementItem) {
            itemsToCopy.splice(i, 0, {
              ...elementItem,
              _referencedBy: [sceneNode.uid]
            });
            i++;
          }
          // If the element has an image, add that too
          if (elementData.imageId != null) {
            const image = imageStore.items[elementData.imageId];
            if (image && image.mediaUUID != null) {
              const mediaData = imageStore.mediaData[image.id];
              // add the image to the items to be copied
              itemsToCopy.splice(i, 0, { ...image, data: mediaData, _referencedBy: [sceneNode.uid] });
              i++;
            }
          }

          
        } else if (isSceneChartNode(sceneNode)) {
          const chartData = sceneNode.data;
          // Add the referenced chart to the items to be copied
          const chartItem = modelStore.getItem(chartData.chartUid);
          if (chartItem) {
            itemsToCopy.splice(i, 0, {
              ...chartItem,
              _referencedBy: [sceneNode.uid]
            });
            i++;
          }
        } else if (isSceneMediumNode(sceneNode)) {
          const mediumData = sceneNode.data;
          if (mediumData.mediaUUID == null) continue;
          // Add the referenced medium to the items to be copied
          const mediumItem = useMediaStore().items[mediumData.mediaUUID];
          if (!mediumItem) continue;
          const data = mediumItem.data;
          
          sceneNode.data.data = data; // include the media data in the scene node, so we can create a copy of the medium when pasting
        }
      }
    }

    return {
      data: itemsToCopy,
      html: itemsToCopy.map(itemToHTML).join("\n")
    };
  }

  function itemToHTML(item: PastableItem): string {
    if (item.type == "image") {
      return `<img src="${item.data}" style="width: ${item.width}px; height: ${item.height}px; object-fit: ${item.objectFit};">`;
    }

    const htmlBuilder = buildHTMLText(item);
    if ("title" in item) {
      htmlBuilder.addTitle(item.title || "")
    }
    if ("description" in item) {
      htmlBuilder.addHTMLText(item.description);
    }

    // indicators:
    if (item.type == "target") {
      const indicators = indicatorStore.byTarget[item.id] || [];
      if (indicators.length > 0) {
        htmlBuilder
          .addTitle(t("objectNames.indicator", 5), 3)
          .addList(indicators.map((i) => i.title));
      }
    }

    // value:
    if ("value" in item) {
      htmlBuilder
        .addTitle(
          item.type == "target" ? t("labels.status") : t("labels.value"),
          3
        )
        .addText(item.value + item.unit);
    }

    return htmlBuilder.toString();
  }

  function notifyAboutCopy(copyableItems: PastableItem[]) {
    // Notify user via toast that items have been copied
    if (copyableItems.length <= 0) return;

    if (copyableItems.length == 1) {
      const title = getTitle(copyableItems[0]);
      const message = t("messages.xCopiedToClipboard", {
        title: title
      });
      toast.success(message, { timeout: 2500 });
      return;
    }

    const message = t("messages.itemsCopied", copyableItems.length);
    toast.success(message, { timeout: 2500 });
  }

  /**
   * Called when the user presses CTRL/CMD + V.
   * Pastes the items from the clipboard.
   * (creates new items, migrates them to the new structure)
   */
  onPasteEvent(async (items) => {
    if (!items || items.length == 0) {
      console.warn("No items to paste");
      return;
    }

    const pastedItems: PastableItem[] = [];

    const undoStore = useUndo();
    undoStore.startGroup("model", "Paste items");

    const references = new Map<Uid<string>, PastableItem[]>();

    for (const item of items) {
      if (item._referencedBy) {
        for (const ref of item._referencedBy) {
          const referencesForRef = references.get(ref) || [];
          referencesForRef.push(item);
          references.set(ref, referencesForRef);
        }
        continue;
      }

      console.log("paste item", item);

      switch (item.type) {
        case "element":
        case "target": {
          const targetsWithSameTitle = targetStore.orderedItems.filter((t) => t.title === item.title);
          if (targetsWithSameTitle.length > 0) {
            item.title = `${item.title}${t("labels.copySuffix")}`;
          }
          const newTarget = await targetStore.importItem(item);
          pastedItems.push(newTarget);
          break;
        }
        case "resource": {
          const resourcesWithSameTitle = resourceStore.orderedItems.filter((r) => r.title === item.title);
          if (resourcesWithSameTitle.length > 0) {
            item.title = `${item.title}${t("labels.copySuffix")}`;
          }
          const newResource = await resourceStore.importItem(item);
          pastedItems.push(newResource);
          break;
        }
        case "index": {
          const indicesWithSameTitle = indexStore.orderedItems.filter((i) => i.title === item.title);
          if (indicesWithSameTitle.length > 0) {
            item.title = `${item.title}${t("labels.copySuffix")}`;
          }
          const newIndex = await indexStore.importItem(item);
          pastedItems.push(newIndex);
          break;
        }
        case "image": {
          // create a scene medium for the image
          const medium = await sceneMediumStore.create({
            data: {
              data: item.data,
              mediaType: item.mediaType,
            },
            width: item.width,
            height: item.height,
          });
          pastedItems.push(medium);
          break;
        }
        case "sceneNode": {
          // If the scene already contains a node referencing the same element,
          // we definitely have to copy the element, otherwise we have multiple 
          // nodes referencing the same element, which should never happen.
          const elementUid = item.data.elementUid;
          const nodesWithSameElement = sceneNodeStore.orderedItems.filter((n) => n.sceneId === sceneTree.activeScene.id && isSceneElementNode(n) && n.data.elementUid === elementUid);
          const forceElementCopy = nodesWithSameElement.length > 0;

          const nodeReferences = references.get(item.uid) || [];
          for (const ref of nodeReferences) {
            if (ref.type == "image") {
              const existingImage = imageStore.items[ref.id];
              if (existingImage) {
                console.log("referenced image already exists, skipping import", ref);
              } else {
                try {
                  const newImage = await imageStore.importItem(ref);
                  item.data.imageId = newImage.id;
                } catch (error) {
                  console.warn("could not duplicate image, removing reference.", error);
                  item.data.imageId = null;
                }
              }
            }
            if (ref.type == "target" || ref.type == "resource" || ref.type == "index") {
              const elementItem = modelStore.getItem(ref.uid);
              if (forceElementCopy || elementItem == null || elementItem.createdAt != ref.createdAt) {
                if (forceElementCopy) {
                  ref.title = `${ref.title}${t("labels.copySuffix")}`;
                }
                if (ref.type == "target") {
                  const newTarget = await targetStore.importItem(ref);
                  item.data.elementUid = newTarget.uid;
                } else if (ref.type == "resource") {
                  const newResource = await resourceStore.importItem(ref);
                  item.data.elementUid = newResource.uid;
                } else if (ref.type == "index") {
                  const newIndex = await indexStore.importItem(ref);
                  item.data.elementUid = newIndex.uid;
                }
              } else {
                console.log("reference already exists, skipping import", ref);
              }
            }
            if (ref.type == "chart") {
              console.log("always copy chart and create new reference.");
              const newChart = await useChartStore().importItem(ref);
              item.data.chartUid = newChart.uid;
            }
          }
          item.sceneId = sceneTree.activeScene.id;
          item.parentSceneNodeUid = undefined; // clear parent id, nodes will be added to the root of the current scene

          const nodesAtSameX = sceneNodeStore.orderedItems.filter((n) => n.sceneId === item.sceneId && n.x === item.x);
          if (nodesAtSameX.length > 0) {
            item.x += 20;
          }
          const nodesAtSameY = sceneNodeStore.orderedItems.filter((n) => n.sceneId === item.sceneId && n.y === item.y);
          if (nodesAtSameY.length > 0) {
            item.y += 20;
          }

          const newNode = await sceneNodeStore.importItem(item);
          pastedItems.push(newNode);
          break;
        }
        case "text/plain": {
          if (typeof item.data != "string") {
            throw new Error(`Expected text data, got ${typeof item.data}`);
          }
          if (item.data.startsWith("<")) {
            // try to parse as SVG
            const parser = new DOMParser();
            try {
              const doc = parser.parseFromString(item.data, "image/svg+xml");
              const svgElement = doc.querySelector("svg");
              if (!svgElement) {
                throw new Error("No SVG element found");
              }
              const svgText = new XMLSerializer().serializeToString(svgElement);
              const dataURL = `data:image/svg+xml;base64,${btoa(svgText)}`;
              
              const img = new Image();
              img.src = dataURL;
              await new Promise((resolve, reject) => {
                img.onload = async () => {
                  const width = img.width;
                  const height = img.height;

                  const medium = await sceneMediumStore.create({
                    data: {
                      data: dataURL,
                      mediaType: "image/svg+xml",
                    },
                    width,
                    height,
                  });

                  resolve(medium);
                };
                img.onerror = (e) => {
                  console.warn("Could not load SVG", e);
                  reject(e);
                };
              });
              console.log("pasting SVG as scene medium", svgElement);
              break;
            } catch (e) {
              console.warn("Could not parse as SVG", e);
            }
          }
          console.log("pasting text as scene text");
          await sceneTextStore.create({
            data: {
              text: item.data
            }
          });
          break;
        }
        case "chart": {
          const newChart = await useChartStore().importItem(item);
          pastedItems.push(newChart);
          break;
        }
        default: {
          throw new Error(`Unknown item type ${item.type}`);
        }
      }
    }

    const selectableItems = pastedItems.filter(
      (i): i is TargetItem | ResourceItem | IndexItem | SceneNodeItem =>
        i.type != "image"
    );

    selectionStore.selectMany(selectableItems);

    undoStore.endGroup("model");
  });

  function copyItems(items: { type: string; id: number }[]) {
    const copyableItems = getCopyableItems(items);
    const copydata = getCopyData(copyableItems);
    if (copydata.data.length == 0) {
      console.warn("No data to copy");
      return;
    }

    notifyAboutCopy(copyableItems);

    copy(copydata);
  }

  function getTitle(item: PastableItem): string {
    if ("title" in item && item.title != null) {
      return `${t("quote.open")}${item.title}${t("quote.close")}`;
    }
    if (item.type === "sceneNode") {
      if (isSceneElementNode(item)) {
        const elementItem = modelStore.getItem(item.data.elementUid);
        if (!elementItem) {
          return t("objectNames.element");
        }
        return getTitle(elementItem);
      }
      return t(`objectNames.${item.dataType}`, 1);
    }
    return t(`objectNames.${item.type}`, 1);
  }

  return { copyItems };
}
